import React, { useState, useEffect } from 'react';
import { X, Loader2, Check, AlertCircle, FileText, Image } from 'lucide-react';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import { toast } from 'react-toastify';
import {
  sendReceipt,
  validatePhoneNumber,
  formatPhoneNumber,
  normalizePhoneNumber,
  getNetworkProvider
} from '../../api/whatsapp';
import { getReceiptConfig, getReceiptData } from '../../api/receipts';

/**
 * WhatsAppSendModal Component
 * Modal for sending receipt via WhatsApp
 */
const WhatsAppSendModal = ({ sale, onClose, onSuccess }) => {
  const [phoneNumber, setPhoneNumber] = useState(sale.customer_phone || '');
  const [sending, setSending] = useState(false);
  const [validation, setValidation] = useState({ isValid: false, message: '' });
  const [networkProvider, setNetworkProvider] = useState('');
  const [format, setFormat] = useState('pdf'); // 'pdf' or 'image'
  
  // State for fetched sale data (with correct combined total for joint sales)
  const [saleData, setSaleData] = useState({
    receipt_number: sale.receipt_number,
    customer_name: sale.customer_name,
    total_amount: sale.total_amount
  });
  const [loadingSaleData, setLoadingSaleData] = useState(true);

  // Fetch correct sale data (with combined total for joint sales)
  useEffect(() => {
    const fetchSaleData = async () => {
      try {
        setLoadingSaleData(true);
        const response = await getReceiptData(sale.id);
        if (response && response.data) {
          const data = response.data;
          setSaleData({
            receipt_number: data.display_receipt_number || data.receipt_number || sale.receipt_number,
            customer_name: data.customer_name || sale.customer_name,
            total_amount: data.total_amount || sale.total_amount
          });
        }
      } catch (error) {
        console.error('Failed to load sale data for WhatsApp modal:', error);
        // Fall back to the prop data
      } finally {
        setLoadingSaleData(false);
      }
    };
    
    if (sale.id) {
      fetchSaleData();
    } else {
      setLoadingSaleData(false);
    }
  }, [sale.id]);

  // Fetch receipt configuration for default format
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await getReceiptConfig();
        const config = response.data;
        if (config.delivery_format === 'image') {
          setFormat('image');
        } else {
          setFormat('pdf');
        }
      } catch (error) {
        console.error('Failed to load receipt config:', error);
      }
    };
    fetchConfig();
  }, []);

  // Validate phone number on change
  useEffect(() => {
    if (phoneNumber.trim()) {
      const result = validatePhoneNumber(phoneNumber);
      setValidation(result);

      if (result.isValid) {
        const provider = getNetworkProvider(phoneNumber);
        setNetworkProvider(provider);
      } else {
        setNetworkProvider('');
      }
    } else {
      setValidation({ isValid: false, message: '' });
      setNetworkProvider('');
    }
  }, [phoneNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    // Validate phone number
    const validationResult = validatePhoneNumber(phoneNumber);
    if (!validationResult.isValid) {
      toast.error(validationResult.message);
      return;
    }

    setSending(true);

    try {
      // Normalize phone number to international format
      const normalizedPhone = normalizePhoneNumber(phoneNumber);

      await sendReceipt({
        saleId: sale.id,
        phoneNumber: normalizedPhone,
        format: format
      });

      toast.success(`Receipt sent successfully via WhatsApp as ${format.toUpperCase()}`);

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('WhatsApp send error:', error);
      toast.error(error.response?.data?.message || 'Failed to send receipt via WhatsApp');
    } finally {
      setSending(false);
    }
  };

  const handlePhoneChange = (e) => {
    // Allow only digits, +, spaces, and hyphens
    const value = e.target.value.replace(/[^\d+\s-]/g, '');
    setPhoneNumber(value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-lightGray">
          <div className="flex items-center gap-2">
            <WhatsAppIcon className="w-5 h-5 text-accent-gold" />
            <h2 className="text-lg font-semibold text-primary-black">
              Send via WhatsApp
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="text-secondary-gray hover:text-primary-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* Receipt Info */}
          <div className="bg-secondary-offWhite p-3 rounded-lg mb-4 border-2 border-accent-gold">
            <p className="text-sm text-secondary-gray">Receipt Number</p>
            <p className="font-semibold text-primary-black">{saleData.receipt_number}</p>
            <p className="text-sm text-secondary-gray mt-2">Customer</p>
            <p className="font-medium text-primary-black">{saleData.customer_name}</p>
            <p className="text-sm text-secondary-gray mt-2">Total Amount</p>
            {loadingSaleData ? (
              <p className="font-semibold text-accent-gold flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </p>
            ) : (
              <p className="font-semibold text-accent-gold">
                ₦{parseFloat(saleData.total_amount).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
              </p>
            )}
          </div>

          {/* Format Toggle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-primary-black mb-2">
              Receipt Format
            </label>
            <div className="flex rounded-lg border border-secondary-lightGray overflow-hidden">
              <button
                type="button"
                onClick={() => setFormat('pdf')}
                disabled={sending}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  format === 'pdf'
                    ? 'bg-accent-gold text-white'
                    : 'bg-white text-secondary-gray hover:bg-secondary-offWhite'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FileText className="w-4 h-4" />
                <span>PDF Document</span>
              </button>
              <button
                type="button"
                onClick={() => setFormat('image')}
                disabled={sending}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-l border-secondary-lightGray ${
                  format === 'image'
                    ? 'bg-accent-gold text-white'
                    : 'bg-white text-secondary-gray hover:bg-secondary-offWhite'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Image className="w-4 h-4" />
                <span>Image (PNG)</span>
              </button>
            </div>
          </div>

          {/* Phone Number Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-primary-black mb-2">
              WhatsApp Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="+234 801 234 5678 or 08012345678"
                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  phoneNumber && validation.isValid
                    ? 'border-green-500 focus:ring-green-500'
                    : phoneNumber && !validation.isValid
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-secondary-lightGray focus:ring-accent-gold'
                }`}
                required
                disabled={sending}
              />
              {phoneNumber && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {validation.isValid ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>

            {/* Validation Message */}
            {phoneNumber && validation.message && (
              <p
                className={`text-sm mt-1 ${
                  validation.isValid ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {validation.message}
                {validation.isValid && networkProvider && networkProvider !== 'Unknown' && (
                  <span className="ml-1 text-secondary-gray">
                    ({networkProvider})
                  </span>
                )}
              </p>
            )}

            {/* Format Hint */}
            <p className="text-xs text-secondary-gray mt-1">
              Format: +234 801 234 5678 or 08012345678
            </p>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> The receipt will be sent as {format === 'pdf' ? 'a PDF document' : 'an image (PNG)'} via WhatsApp.
              Make sure the phone number is registered on WhatsApp.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="flex-1 px-4 py-2 border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !validation.isValid}
              className="flex-1 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <WhatsAppIcon className="w-4 h-4" />
                  <span>Send via WhatsApp</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WhatsAppSendModal;

